import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/base/EmptyState";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = newsList.find((news) => news.id === id);

  const related = useMemo(() => {
    if (!item) return [];
    return newsList
      .filter((news) => news.id !== item.id)
      .sort((a, b) => {
        const aMatch = a.category === item.category ? 1 : 0;
        const bMatch = b.category === item.category ? 1 : 0;
        if (aMatch !== bMatch) return bMatch - aMatch;
        return a.date < b.date ? 1 : -1;
      })
      .slice(0, 3);
  }, [item]);

  useSeo({
    title: item ? `${item.title} - 星空传媒影视资讯` : "资讯详情 - 星空传媒",
    description: item
      ? item.summary
      : "星空传媒影视资讯详情页，了解最新影视动态与行业观察。",
    keywords: item
      ? `星空传媒,影视资讯,${item.category},${item.title}`
      : "星空传媒,影视资讯,资讯详情",
  });

  if (!item) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-16">
        <EmptyState
          icon="ri-newspaper-line"
          title="未找到该条资讯"
          description="这条资讯可能已被移除或链接有误，返回资讯列表继续阅读吧。"
        />
        <div className="flex justify-center">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-600 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-accent-600 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/news" className="transition-colors hover:text-accent-600 cursor-pointer">
          影视资讯
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="max-w-[200px] truncate text-foreground-900 md:max-w-none">{item.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <span className="inline-flex items-center rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-700">
            {item.category}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
            {item.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {item.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              来源：{item.source}
            </span>
          </div>

          <div className="mt-5 overflow-hidden rounded-lg bg-secondary-100">
            <img
              src={item.cover}
              alt={`${item.title} 配图`}
              title={`${item.title} - 星空传媒影视资讯`}
              className="w-full object-cover object-top"
            />
          </div>

          <div className="mt-6 space-y-4">
            <p className="text-base font-medium leading-relaxed text-foreground-800">
              {item.summary}
            </p>
            {item.content.map((paragraph, index) => (
              <p key={index} className="text-sm leading-loose text-foreground-700 md:text-[15px]">
                {paragraph}
              </p>
            ))}
          </div>

          <p className="mt-8 rounded-md bg-secondary-50 px-4 py-3 text-xs text-foreground-500">
            免责声明：本文为演示资讯内容，用于展示平台资讯聚合与阅读功能，不代表任何真实报道。
          </p>
        </article>

        <aside className="lg:col-span-1">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <span className="h-4 w-1 rounded-full bg-accent-500" />
            相关资讯
          </h2>
          <div className="flex flex-col gap-3">
            {related.map((news) => (
              <NewsCard key={news.id} item={news} layout="horizontal" />
            ))}
          </div>
          <Link
            to="/news"
            className="mt-4 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600 cursor-pointer"
          >
            <i className="ri-list-check"></i>
            查看全部资讯
          </Link>
        </aside>
      </div>
    </div>
  );
}